(() => {
  const cache = new Map();
  let editionsObserver = null;

  const parseJSON = (element) => {
    const script = element.querySelector('[data-bookverse-editions-config]');

    if (!script) return null;

    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      return null;
    }
  };

  const fetchEditions = async (config) => {
    const cacheKey = `${config.workIdField}=${config.workId}`;

    if (cache.has(cacheKey)) {
      return cache.get(cacheKey);
    }

    const request = fetch(config.fetchUrl, { headers: { Accept: 'text/html' } })
      .then((response) => {
        if (!response.ok) throw new Error('BookVerse editions request failed');
        return response.text();
      })
      .then((html) => {
        try {
          return JSON.parse(html);
        } catch (error) {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const script = doc.querySelector('script[type="application/json"]');

          if (script) {
            return JSON.parse(script.textContent);
          }

          const section = doc.querySelector('[id^="shopify-section-"]');
          const sectionText = section?.textContent?.trim();

          return sectionText ? JSON.parse(sectionText) : { products: [] };
        }
      })
      .then((data) => (Array.isArray(data.products) ? data.products : []))
      .catch(() => []);

    cache.set(cacheKey, request);
    return request;
  };

  const normalizeProduct = (product, config) => ({
    ...product,
    workId: product.workId || '',
    format: product.format || config.labels.defaultFormat,
    editionName: product.editionName || product.format || config.labels.defaultFormat,
  });

  const normalizeAudiobookLinks = (value, config) => {
    let links = value;

    if (typeof links === 'string') {
      try {
        links = JSON.parse(links);
      } catch (error) {
        links = [];
      }
    }

    if (!Array.isArray(links)) return [];

    const seenUrls = new Set();

    return links.flatMap((link) => {
      if (!link || typeof link !== 'object') return [];

      const url = String(link.url || '').trim();

      if (!url || seenUrls.has(url)) return [];

      try {
        const parsedUrl = new URL(url, window.location.origin);
        if (!['http:', 'https:'].includes(parsedUrl.protocol)) return [];
      } catch (error) {
        return [];
      }

      seenUrls.add(url);

      const editionName = String(link.editionName || config.labels.audiobook).trim();

      return [
        {
          id: `audiobook:${link.editionId || url}`,
          title: editionName,
          url,
          workId: config.workId,
          available: true,
          priceText: '—',
          format: config.labels.audiobook,
          editionName,
          external: true,
        },
      ];
    });
  };

  const productKey = (product) => String(product.id || product.handle || product.url);

  const dedupeProducts = (products) => {
    const seen = new Set();

    return products.filter((product) => {
      const key = productKey(product);
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  const sortProducts = (products, currentProductId) =>
    [...products].sort((a, b) => {
      const aCurrent = String(a.id) === String(currentProductId);
      const bCurrent = String(b.id) === String(currentProductId);

      if (aCurrent !== bCurrent) return aCurrent ? -1 : 1;

      return `${a.format} ${a.editionName} ${a.title}`.localeCompare(`${b.format} ${b.editionName} ${b.title}`);
    });

  const filterProductsByWorkId = (products, workId) => {
    if (!workId) return [];

    return products.filter((product) => String(product.workId || '') === String(workId));
  };

  const escapeHTML = (value) => {
    const span = document.createElement('span');
    span.textContent = value || '';
    return span.innerHTML;
  };

  const renderStatus = (product, config) => {
    if (product.external) return config.labels.listenNow;
    if (String(product.id) === String(config.currentProduct.id)) return config.labels.current;
    if (!product.available) return config.labels.soldOut;
    return product.preorder ? config.labels.preorder : config.labels.inStock;
  };

  const renderRow = (product, config) => {
    const isCurrent = String(product.id) === String(config.currentProduct.id);
    const status = renderStatus(product, config);
    const title = escapeHTML(product.title);
    const format = escapeHTML(product.format);
    const editionName = escapeHTML(product.editionName || product.title);
    const price = escapeHTML(product.priceText);
    const url = escapeHTML(product.url);
    const externalAttributes = product.external ? ' target="_blank" rel="noopener noreferrer"' : '';
    const editionLink = `<a href="${url}" title="${title}"${externalAttributes}>${editionName}</a>`;
    const statusContent = product.external
      ? `<a href="${url}"${externalAttributes}>${escapeHTML(status)}</a>`
      : `<span class="product-layout__edition-status">${escapeHTML(status)}</span>`;

    return `
      <tr class="${isCurrent ? 'is-current' : ''}">
        <td data-label="${escapeHTML(config.labels.format)}">
          <span>${format}</span>
        </td>
        <td data-label="${escapeHTML(config.labels.edition)}">
          ${
            isCurrent
              ? `<span>${editionName}</span>`
              : editionLink
          }
        </td>
        <td data-label="${escapeHTML(config.labels.price)}">${price}</td>
        <td data-label="${escapeHTML(config.labels.status)}">
          ${statusContent}
        </td>
      </tr>
    `;
  };

  window.BookverseEditions = {
    fetchEditions,
    normalizeProduct,
    filterProductsByWorkId,
    dedupeProducts,
    productKey,
    sortProducts,
  };

  class BookverseEditions extends HTMLElement {
    connectedCallback() {
      this.content = this.querySelector('[data-bookverse-editions-content]');
      this.config = parseJSON(this);
      this.expanded = false;
      this.loaded = false;
      this.onToggleClick = this.onToggleClick.bind(this);

      if (!this.config?.workId || !this.content) return;

      this.content.addEventListener('click', this.onToggleClick);
      this.deferLoad();
    }

    disconnectedCallback() {
      if (this.content) {
        this.content.removeEventListener('click', this.onToggleClick);
      }
    }

    onToggleClick(event) {
      const toggle = event.target.closest('[data-bookverse-editions-toggle]');
      if (!toggle) return;

      this.expanded = !this.expanded;
      this.render();
    }

    async load() {
      if (this.loaded) return;
      this.loaded = true;

      const fetchedProducts = await fetchEditions(this.config);
      const audiobookProducts = normalizeAudiobookLinks(this.config.audiobookLinks, this.config);
      const products = filterProductsByWorkId(
        dedupeProducts([this.config.currentProduct, ...fetchedProducts, ...audiobookProducts])
          .map((product) => normalizeProduct(product, this.config)),
        this.config.workId
      );

      const hasRelatedProduct = products.some(
        (product) => product.external || String(product.id) !== String(this.config.currentProduct.id)
      );

      if (!hasRelatedProduct) {
        this.removeAttribute('data-bookverse-editions-pending');
        this.style.removeProperty('visibility');
        this.style.removeProperty('min-height');
        this.hidden = true;
        return;
      }

      this.products = sortProducts(products, this.config.currentProduct.id);
      this.render();
      this.removeAttribute('data-bookverse-editions-pending');
      this.style.removeProperty('visibility');
      this.style.removeProperty('min-height');
      this.hidden = false;
      this.dispatchEvent(new CustomEvent('bookverse:editions-ready', { bubbles: true, detail: { products: this.products } }));
    }

    deferLoad() {
      if (!('IntersectionObserver' in window) || window.Shopify?.designMode) {
        this.load();
        return;
      }

      if (this.hidden) {
        this.hidden = false;
        this.setAttribute('data-bookverse-editions-pending', 'true');
        this.style.visibility = 'hidden';
        this.style.minHeight = '1px';
      }

      if (!editionsObserver) {
        editionsObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;

              editionsObserver.unobserve(entry.target);
              entry.target.load();
            });
          },
          { rootMargin: '900px 0px' }
        );
      }

      editionsObserver.observe(this);
    }

    render() {
      const visibleLimit = 5;
      const visibleProducts = this.expanded ? this.products : this.products.slice(0, visibleLimit);
      const shouldShowToggle = this.products.length > visibleLimit;
      const toggleLabel = this.expanded
        ? this.config.labels.seeLess
        : `${this.config.labels.seeMore} (${this.products.length - visibleLimit})`;

      this.content.innerHTML = `
        <div class="product-layout__all-editions-header">
          <h2>${escapeHTML(this.config.heading)}</h2>
        </div>
        <div class="product-layout__all-editions-table-wrap">
          <table>
            <thead>
              <tr>
                <th scope="col">${escapeHTML(this.config.labels.format)}</th>
                <th scope="col">${escapeHTML(this.config.labels.edition)}</th>
                <th scope="col">${escapeHTML(this.config.labels.price)}</th>
                <th scope="col">${escapeHTML(this.config.labels.status)}</th>
              </tr>
            </thead>
            <tbody>
              ${visibleProducts.map((product) => renderRow(product, this.config)).join('')}
            </tbody>
          </table>
        </div>
        ${
          shouldShowToggle
            ? `<button class="product-layout__all-editions-toggle" type="button" data-bookverse-editions-toggle>${escapeHTML(toggleLabel)}</button>`
            : ''
        }
      `;
    }
  }

  if (!customElements.get('bookverse-editions')) {
    customElements.define('bookverse-editions', BookverseEditions);
  }
})();
